import { CASE_TYPE_ID, CONSTANTS, licenceList } from 'common/constants';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { dispatch, useSelector } from 'store';
import { requestDetails, requestPMNDetails } from 'store/api/application.api';
import { updateStatus, updateUploadStatus } from 'store/slices/application.slice';
import { formatRetrieve } from 'features/applicationPage/payload';
import { DetailsType, FieldValue, FormData, IPreviewPage, Industry } from 'types/application';
import { INDUSTRIES } from 'common/application.const';

export const Context = createContext<IPreviewPage | null>(null);
function PreviewProvider({ children }: { children: React.ReactElement }) {
    const { user } = useSelector((state) => state.account);
    const [details, setDetails] = useState<DetailsType>({
        forms: [],
        hasStarted: false
    });
    const [caseInfo, setCaseInfo] = useState({
        pzInsKey: '',
        caseid: '',
        saveMessageEndpoint: '',
        fetchMessageEndpoint: '',
        fetchFileMessage: ''
    });
    const [consumerAdvice, setConsumerAdvice] = useState('');
    const [ratingDescription, setRatingDescription] = useState('');
    const [decisionCode, setDecisionCode] = useState('');
    const [remarksForApplicant, setRemarksForApplicant] = useState('');
    const [application, setApplication] = useState('');
    const [applicationForm, setApplicationForm] = useState('');
    const [licenceName, setLicenceName] = useState('');
    const [isMessagingThreadDisableForFVG, setIsMessagingThreadDisableForFVG] = useState('');
    const industry = (INDUSTRIES as Industry[]).find((industry: Industry) => industry.caseType === application);
    const [industryCopy, setIndustryCopy] = useState(industry);
    const [invalidAccess, setInvalidAccess] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const location = useLocation();
    const { cases, licences, completedLicences } = useSelector((state) => state.dashboard);
    const listCompletedLicense = licenceList;
    const listStatusDisabledMessage = [
        'Approved',
        'Rejected',
        'Expired',
        'Cancelled',
        'Refused',
        'Refused Certification',
        'Withdrawn',
        'Certified',
        'Notified'
    ];

    const encodedID = location.pathname.split('/')[2];

    const decodeIDFromURL = (id: string) => {
        return decodeURIComponent(id);
    };

    const originalCaseID = decodeIDFromURL(encodedID);

    const caseTypeID = cases.find((value: { [key: string]: string }) => value.caseid === originalCaseID);
    const licenceTypeID = licences.find((value: { [key: string]: string }) => value.caseid === originalCaseID);
    const completedLicenceTypeID = completedLicences.find((value: { [key: string]: string }) => {
        if (listCompletedLicense.includes(value?.licenceName)) {
            return (
                value.applicationNumber === location.pathname.split('/')[2] ||
                (value?.pxPages as any)?.AEL?.caseid === location.pathname.split('/')[2].replace(/-/g, '/') ||
                value.caseid === location.pathname.split('/')[2].replace(/-/g, '/')
            );
        }
        return (
            value.caseid === location.pathname.split('/')[2].replace(/-/g, '/') ||
            value.applicationNumber === location.pathname.split('/')[2]
        );
    });

    const appStatus = details?.forms[0]?.values?.['summary-status'] as string;
    let isMessageDisabled = false;
    if (appStatus) {
        isMessageDisabled = listStatusDisabledMessage.includes(appStatus);
    }

    const appID = details?.forms[0]?.values?.['summary-id'] as string;
    // Check if ID includes "AEC"
    const idIncludesAEC = appID?.includes('AEC');

    if (idIncludesAEC && appStatus === 'Certified') {
        const AECEventDates = details?.forms[2]?.values?.['event-details-event-date-time'] as { from: string; to: string }[];
        const currentDate = new Date();
        // Check if any event "to" date is active to the current date
        const hasActiveEventDate = AECEventDates?.some((event) => new Date(event.to) > currentDate) || false;
        isMessageDisabled = !hasActiveEventDate;
    }

    const fetchDetails = async () => {
        setInitialLoading(true);
        if (caseTypeID || licenceTypeID || completedLicenceTypeID) {
            dispatch(updateStatus(CONSTANTS.LOADING));
            try {
                let response;
                let caseType;
                if (licenceTypeID) {
                    response =
                        licenceTypeID?.licenceName === 'Public Mobile Network Licence' ||
                        licenceTypeID?.licenceName === 'Fixed Wireless Network Licence' ||
                        licenceTypeID?.licenceName === 'Wide-Area Private Network Licence'
                            ? await requestPMNDetails(licenceTypeID?.pzInsKey as string)
                            : await requestDetails(licenceTypeID?.pzInsKey as string);
                    setCaseInfo({
                        pzInsKey: licenceTypeID?.pzInsKey,
                        caseid: licenceTypeID?.caseid,
                        saveMessageEndpoint: '/licmessagethread',
                        fetchMessageEndpoint: 'GETLICENCEMESSAGES',
                        fetchFileMessage: 'GETLICENCEMESSAGEFILE'
                    });
                } else {
                    response =
                        completedLicenceTypeID?.licenceName === 'Public Mobile Network Licence' ||
                        completedLicenceTypeID?.licenceName === 'Fixed Wireless Network Licence' ||
                        completedLicenceTypeID?.licenceName === 'Wide-Area Private Network Licence'
                            ? await requestPMNDetails((caseTypeID?.pzInsKey ?? completedLicenceTypeID?.pzInsKey) as string)
                            : await requestDetails((caseTypeID?.pzInsKey ?? completedLicenceTypeID?.pzInsKey) as string);
                    
                    // Determine if this is FVG case type (covers all FVG applications including Films, VideoGames, RCA, etc.)
                    const isFVG = response?.caseTypeID?.startsWith('IMDA-FVGCS-Work-FVG-');
                    
                    setCaseInfo({
                        pzInsKey: (caseTypeID?.pzInsKey ??
                            (completedLicenceTypeID?.pxPages as any)?.AEL?.pzInsKey ??
                            completedLicenceTypeID?.pzInsKey) as string,
                        caseid: (caseTypeID?.caseid ??
                            (completedLicenceTypeID?.pxPages as any)?.AEL?.caseid ??
                            completedLicenceTypeID?.caseid) as string,
                        saveMessageEndpoint: isFVG ? '/fvgmessagethread' : '/messagethread',
                        fetchMessageEndpoint: 'GETMESSAGES',
                        fetchFileMessage: 'GETMESSAGEFILE'
                    });
                }

                if (response?.caseTypeID === 'IMDA-FVGCS-Work-FVG-FilmTitles') {
                    caseType = response.caseTypeID.replace('Titles', 's');
                } else if (response?.caseTypeID === 'IMDA-FVGCS-Work-FVG-RCAFlow') {
                    caseType = response.caseTypeID.replace('RCAFlow', 'Films');
                } else if (response?.caseTypeID === 'IMDA-FVGCS-Work-FVG-RCARegistration') {
                    caseType = response.caseTypeID.replace('RCARegistration', 'Films');
                } else {
                    if (
                        licenceTypeID?.licenceName === 'Public Mobile Network Licence' ||
                        completedLicenceTypeID?.licenceName === 'Public Mobile Network Licence' ||
                        licenceTypeID?.licenceName === 'Fixed Wireless Network Licence' ||
                        completedLicenceTypeID?.licenceName === 'Fixed Wireless Network Licence' ||
                        licenceTypeID?.licenceName === 'Wide-Area Private Network Licence' ||
                        completedLicenceTypeID?.licenceName === 'Wide-Area Private Network Licence'
                    ) {
                        caseType = response.pxObjClass;
                    } else {
                        caseType = response.caseTypeID;
                    }
                }

                // ROE variant resolution: map generic LICApplication to ROE-specific case types
                if (response?.caseTypeID === 'IMDA-IRIS-Work-LICApplication' && response?.content?.licenceName === 'Radio Operator Exam') {
                    // Determine if this is a view context (licence) vs creation context (application)
                    const isLicenceView = Boolean(licenceTypeID || completedLicenceTypeID);
                    const newCaseType = isLicenceView ? 'IMDA-IRIS-Work-LICApplication-ROE-VIEW' : 'IMDA-IRIS-Work-LICApplication-ROE';
                    caseType = newCaseType;
                }

                // ACA variant resolution: map generic LICApplication to ACA-specific case types
                if (
                    response?.caseTypeID === 'IMDA-IRIS-Work-LICApplication' &&
                    response?.content?.licenceName === 'Accreditation of Certification Authority'
                ) {
                    // Determine if this is a view context (licence) vs creation context (application)
                    const isLicenceView = Boolean(licenceTypeID || completedLicenceTypeID);
                    // Check if this is an amend application
                    const isAmend =
                        response?.content?.transactionType === 'Amend' || response?.content?.LICApplication?.transactionType === 'Amend';

                    let newCaseType;
                    if (isLicenceView) {
                        newCaseType = isAmend ? 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW' : 'IMDA-IRIS-Work-LICApplication-ACA-VIEW';
                    } else {
                        newCaseType = 'IMDA-IRIS-Work-LICApplication-ACA';
                    }
                    caseType = newCaseType;
                }

                // AA variant resolution: map generic LICApplication to AA-specific case types
                if (
                    response?.caseTypeID === 'IMDA-IRIS-Work-LICApplication' &&
                    response?.content?.licenceName === 'Registration of Accounting Authority'
                ) {
                    // Determine if this is a view context (licence) vs creation context (application)
                    const isLicenceView = Boolean(licenceTypeID || completedLicenceTypeID);
                    const newCaseType = isLicenceView ? 'IMDA-IRIS-Work-LICApplication-AA-VIEW' : 'IMDA-IRIS-Work-LICApplication-AA';
                    caseType = newCaseType;
                }

                setApplication(caseType);
                if (Object.keys(response).length > 0) {
                    dispatch(updateUploadStatus(CONSTANTS.LOADING));
                    let formattedForms;
                    if (completedLicenceTypeID) {
                        if (
                            completedLicenceTypeID?.licenceName !== 'Public Mobile Network Licence' &&
                            completedLicenceTypeID?.licenceName !== 'Fixed Wireless Network Licence' &&
                            completedLicenceTypeID?.licenceName !== 'Wide-Area Private Network Licence'
                        ) {
                            // Use the updated caseType instead of response.caseTypeID
                            formattedForms = await formatRetrieve(
                                response?.content,
                                caseType || response.caseTypeID,
                                completedLicenceTypeID?.pzInsKey
                            );
                        } else {
                            formattedForms = await formatRetrieve(response, response.pxObjClass, completedLicenceTypeID?.pzInsKey);
                        }
                    } else {
                        if (response?.content) {
                            formattedForms = await formatRetrieve(
                                response?.content,
                                caseType || response.caseTypeID,
                                (caseTypeID?.pzInsKey ?? licenceTypeID?.pzInsKey) as string,
                                '',
                                '',
                                '',
                                user?.role,
                                undefined,
                                undefined,
                                undefined,
                                false
                            );
                        } else {
                            formattedForms = await formatRetrieve(
                                response,
                                caseType || response.caseTypeID,
                                (caseTypeID?.pzInsKey ?? licenceTypeID?.pzInsKey) as string,
                                '',
                                '',
                                '',
                                user?.role,
                                undefined,
                                undefined,
                                undefined,
                                false
                            );
                        }
                    }
                    if (formattedForms) dispatch(updateUploadStatus(CONSTANTS.FULFILLED));

                    // Extract forms array - handle both array and object { forms, amendType } formats
                    let formsArray: any[] = [];
                    if (
                        formattedForms &&
                        typeof formattedForms === 'object' &&
                        !Array.isArray(formattedForms) &&
                        'forms' in formattedForms
                    ) {
                        // Object format (ACA with amendType)
                        formsArray = (formattedForms as any).forms || [];
                    } else if (Array.isArray(formattedForms)) {
                        // Array format (legacy)
                        formsArray = formattedForms;
                    }

                    const formatted: DetailsType = {
                        forms: formsArray.map((form: FormData) => {
                            const valuesDefaultEmpty = Object.keys(form.values as { [key: string]: FieldValue }).reduce(
                                (values: { [key: string]: FieldValue }, key: string) => {
                                    values[key] = form.values ? form.values[key] || '' : '';
                                    return values;
                                },
                                {}
                            );

                            form.values = valuesDefaultEmpty;
                            return form;
                        }),
                        hasStarted: false
                    };
                    const { ConsumerAdvice = '', RatingDescription = '' } = formatted?.forms[2]?.values as Record<string, string>;
                    setConsumerAdvice(
                        response?.content?.FVGApplication?.ClassificationDecision?.ConsumerAdvice ||
                            response?.content?.FilmTitleDetails?.ClassificationDecision?.ConsumerAdvice ||
                            ConsumerAdvice ||
                            '-'
                    );
                    setRatingDescription(
                        response?.content?.FVGApplication?.ClassificationDecision?.Rating ||
                            response?.content?.FilmTitleDetails?.ClassificationDecision?.FilmsRating ||
                            RatingDescription ||
                            '-'
                    );
                    setDecisionCode(
                        response?.content?.FVGApplication?.ClassificationDecision?.DecisionCode ||
                            response?.content?.FilmTitleDetails?.ClassificationDecision?.DecisionCode ||
                            '-'
                    );
                    setRemarksForApplicant(response?.content?.FVGApplication?.ClassificationDecision?.RemarksforApplicant || '-');
                    setDetails(formatted);
                    setApplicationForm(response?.content?.ApplicationForm);
                    setLicenceName(response?.licenceName || response?.content?.licenceName);
                    setIsMessagingThreadDisableForFVG(response?.content?.IsMessageThreadClosed);
                    dispatch(updateStatus(CONSTANTS.FULFILLED));
                }
            } catch (error) {
                console.log(error);
                dispatch(updateStatus(CONSTANTS.FAILED));
            }
        } else {
            setInvalidAccess(true);
        }
        setInitialLoading(false);
    };

    useEffect(() => {
        fetchDetails();

        const newIndustry = (INDUSTRIES as Industry[]).find((industry: Industry) => industry.caseType === application);

        setIndustryCopy(newIndustry);

        if (application?.startsWith('IMDA-IRIS-Work-LICApplication') || application === 'IMDA-IRIS-Work-Licence') {
            const doYouHaveThePostalCode = details?.forms[3]?.values?.['do-you-have-the-postal-code'];
            const doYouHaveTheMukimOrLandLotNumber = details?.forms[3]?.values?.['do-you-have-the-mukim-or-land-lot-number'];
            const anyLandFixedStation = details?.forms[3]?.values?.['land-fixed-station-any-land-fixed-station'];
            const stationTabs: any = details?.forms[3]?.values?.['station-details-station-tabs'];

            if (doYouHaveThePostalCode !== 'Yes') {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (form.formID === 'localised-private' || form.formID === 'amend-localised-private') {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs-2') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs.map((tab: any) => {
                                                            return {
                                                                ...tab,
                                                                tabContent: tab.tabContent?.filter(
                                                                    (content: any) =>
                                                                        ![
                                                                            'building-name',
                                                                            'postal-code',
                                                                            'street-name',
                                                                            'block-house-no',
                                                                            'floor-level-optional',
                                                                            'unit-optional'
                                                                        ].includes(content.id)
                                                                )
                                                            };
                                                        })
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (doYouHaveThePostalCode !== 'No') {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (form.formID === 'localised-private' || form.formID === 'amend-localised-private') {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs-2') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs.map((tab: any) => {
                                                            return {
                                                                ...tab,
                                                                tabContent: tab.tabContent?.filter(
                                                                    (content: any) =>
                                                                        content.id !== 'do-you-have-the-mukim-or-land-lot-number'
                                                                )
                                                            };
                                                        })
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (doYouHaveTheMukimOrLandLotNumber !== 'Yes') {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (form.formID === 'localised-private' || form.formID === 'amend-localised-private') {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs-2') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs.map((tab: any) => {
                                                            return {
                                                                ...tab,
                                                                tabContent: tab.tabContent?.filter(
                                                                    (content: any) => content.id !== 'mukim-land-lot-number'
                                                                )
                                                            };
                                                        })
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (doYouHaveTheMukimOrLandLotNumber !== 'No') {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (form.formID === 'localised-private' || form.formID === 'amend-localised-private') {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs-2') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs.map((tab: any) => {
                                                            return {
                                                                ...tab,
                                                                tabContent: tab.tabContent?.filter(
                                                                    (content: any) =>
                                                                        ![
                                                                            'latitude',
                                                                            'latitude-degrees',
                                                                            'latitude-minutes',
                                                                            'latitude-seconds',
                                                                            'longitude',
                                                                            'longitude-degrees',
                                                                            'longitude-minutes',
                                                                            'longitude-seconds'
                                                                        ].includes(content.id)
                                                                )
                                                            };
                                                        })
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (anyLandFixedStation !== 'Yes') {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (form.formID === 'localised-private' || form.formID === 'amend-localised-private') {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs-2') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs?.filter((tab: any) => tab.id !== '2')
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (stationTabs?.[0]['fixed-station-licence']?.length === 0) {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (
                            form.formID === 'general-radio-com' ||
                            form.formID === 'amend-general-radio-com' ||
                            form.formID === 'localised-radio-com' ||
                            form.formID === 'amend-localised-radio-com' ||
                            form.formID === 'special-purpose-station-licence' ||
                            form.formID === 'amend-special-purpose-station-licence'
                        ) {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs?.filter((tab: any) => tab.id !== '1')
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }

            if (stationTabs?.[1]['mobile-station-licence']?.length === 0) {
                setIndustryCopy((prevState: any) => {
                    const updatedForms = prevState?.forms.map((form: any) => {
                        if (
                            form.formID === 'general-radio-com' ||
                            form.formID === 'amend-general-radio-com' ||
                            form.formID === 'localised-radio-com' ||
                            form.formID === 'amend-localised-radio-com' ||
                            form.formID === 'special-purpose-station-licence' ||
                            form.formID === 'amend-special-purpose-station-licence'
                        ) {
                            return {
                                ...form,
                                fields: form?.fields.map((section: any) => {
                                    if (section.sectionID === 'station-details') {
                                        return {
                                            ...section,
                                            fieldList: section?.fieldList.map((fieldList: any) => {
                                                if (fieldList.id === 'station-tabs') {
                                                    return {
                                                        ...fieldList,
                                                        tabs: fieldList?.tabs?.filter((tab: any) => tab.id !== '2')
                                                    };
                                                }
                                                return fieldList;
                                            })
                                        };
                                    }
                                    return section;
                                })
                            };
                        }
                        return form;
                    });

                    return {
                        ...prevState,
                        forms: updatedForms
                    };
                });
            }
        } else {
            setIndustryCopy(industry);
        }
    }, [licenceTypeID, application, applicationForm]);

    const value = useMemo(
        () => ({
            details,
            consumerAdvice,
            ratingDescription,
            decisionCode,
            remarksForApplicant,
            application,
            applicationForm,
            licenceName,
            invalidAccess,
            initialLoading,
            caseInfo,
            isMessageDisabled,
            industryCopy,
            isMessagingThreadDisableForFVG
        }),
        [details, initialLoading]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export default PreviewProvider;
